/* eslint-disable @typescript-eslint/no-explicit-any */
import type { VoiceReactionResponse } from '@kidshome/api-contract';
import { tDynamic } from '@/shared/lib/i18n';

/**
 * Resolve a server voice reaction into a ready-to-render line (ADR-0095 §4).
 *
 * The server hands the client a fully-resolved i18n KEY (chosen voice-pack override → neutral
 * fallback) plus a deterministic `variantIndex` and inert render `params` (the opaque `{{name}}` user
 * content, `{{level}}`, etc.). The client never re-derives meaning — it just renders the key in its own
 * locale and picks `variants[variantIndex % len]`:
 *
 *  - the i18n value is an ARRAY of warm variants, so we read it with `returnObjects: true` and index it;
 *  - the server key is namespace-qualified (`play.voice.<id>.<suffix>`); since we translate within the
 *    `play` namespace we strip a leading `play.` so the key resolves;
 *  - `params` carry only inert values — `{{name}}` is opaque user content rendered verbatim via the
 *    `«{{name}}»` token already baked into the catalog strings (ADR-0025), never declined or concatenated.
 *
 * Returns `null` when the key resolves to nothing usable (a retired/unknown key) so the caller can fall
 * back to the calm idle greeting rather than render an empty bubble.
 */
export function renderVoiceLine(
  // `t` is typed with `any` so the heavily-overloaded `TFunction` from `useTranslation('play')` is
  // assignable without tripping the compiler's overload resolution — the same boundary cast `tDynamic`
  // uses. The cast is isolated to this one narrator helper.
  t: (key: any, options?: any) => unknown,
  reaction: Pick<VoiceReactionResponse, 'i18nKey' | 'params' | 'variantIndex'>,
): string | null {
  // The `play` namespace is implied by `useTranslation('play')`; drop the qualifier if present.
  const key = reaction.i18nKey.startsWith('play.')
    ? reaction.i18nKey.slice('play.'.length)
    : reaction.i18nKey;
  const params = (reaction.params ?? {}) as Record<string, unknown>;

  // Ask for the raw value so an array of variants comes back as an array (not a joined string).
  const raw = t(key, { ...params, returnObjects: true, defaultValue: '' });

  if (Array.isArray(raw)) {
    if (raw.length === 0) return null;
    const idx = ((reaction.variantIndex % raw.length) + raw.length) % raw.length;
    // Re-translate the chosen variant by index so interpolation applies to the picked string.
    const line = tDynamic(t, `${key}.${idx}`, params);
    return line.length > 0 ? line : null;
  }

  const single = typeof raw === 'string' ? raw : tDynamic(t, key, params);
  return single.length > 0 ? single : null;
}

/**
 * The calm IDLE greeting shown when no fresh voice reaction exists (the common case) — the SAME content
 * the hero's voice pack authors (`voice.<archetype|neutral>.idle.greeting`), so a prince speaks in his
 * own masculine voice and a princess in hers, instead of the generic, feminine-only
 * `hero.narrator.greeting`. Resolves the archetype-specific idle array first (falling back to the neutral
 * pack, which is gender-safe), and picks deterministically (variant 0) so the line is stable across
 * renders — no flicker.
 *
 * `nsPrefix` lets a caller whose `t` is not scoped to the `play` namespace address it (pass `"play:"`);
 * play-scoped callers (`useTranslation('play')`) pass nothing.
 */
export function idleGreeting(
  t: (key: any, options?: any) => unknown,
  archetypeId: string | null,
  nsPrefix = '',
): string {
  for (const id of [archetypeId, 'neutral']) {
    if (!id) continue;
    const key = `${nsPrefix}voice.${id}.idle.greeting`;
    const raw = t(key, { returnObjects: true, defaultValue: '' });
    if (Array.isArray(raw) && raw.length > 0) return tDynamic(t, `${key}.0`);
  }
  // Final guard: the neutral idle array always exists in the catalogs, so this is unreachable in
  // practice — fall back to the typed neutral idle variant.
  return tDynamic(t, `${nsPrefix}voice.neutral.idle.greeting.0`);
}
